import { Observable, catchError, of, retry } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { PathParseService } from "./path-parse.service";
import { environment } from "../../../environments/environment";
import * as _ from "lodash";

export enum ApiEnum {
	API_ROOT = "root",
}
export type ApiType = ApiEnum.API_ROOT;

@Injectable({
	providedIn: "root",
})
export class HttpDataService {
	constructor(
		private readonly httpClient: HttpClient,
		private readonly pathParseService: PathParseService
	) //private messageService: SnackBarMessageService
	{}

	getHttp$<T>(endpoint: string, params?: any, query?: any[]): Observable<T> {
		return this.httpClient.get<T>(endpoint);
	}

	get$<T>(
		api: ApiType,
		endpoint: string,
		params?: any,
		query?: any[]
	): Observable<T> {
		const { url, parsedParams } = this.manageParams(
			api,
			endpoint,
			params,
			query
		);
		return this.httpClient.get<T>(url, { params: parsedParams });
	}

	getFile$<T>(
		api: ApiType,
		endpoint: string,
		params?: any,
		query?: any[]
	): Observable<T> {
		const { url, parsedParams } = this.manageParams(
			api,
			endpoint,
			params,
			query
		);
		return this.httpClient.get<T>(url, {
			params: parsedParams,
			responseType: "arraybuffer" as "json",
		});
	}

	post$<T>(
		api: ApiType,
		endpoint: string,
		body: any,
		params?: any
	): Observable<T> {
		const { url, parsedParams } = this.manageParams(api, endpoint, params);
		const headers = this.getPostHeaders();
		return this.httpClient.post<T>(url, body, {
			headers,
			params: parsedParams,
		});
	}

	postFile$<T>(
		api: ApiType,
		endpoint: string,
		file: File,
		params?: any
	): Observable<T> {
		const { url, parsedParams } = this.manageParams(api, endpoint, params);
		const formData: FormData = new FormData();
		formData.append("postedFile", file);
		const headers = this.getFileUploadHeaders();

		return this.httpClient.post<T>(url, formData, {
			headers,
			params: parsedParams,
			responseType: "text" as "json",
		});
	}

	patch$<T>(
		api: ApiType,
		endpoint: string,
		body: any,
		params?: any
	): Observable<T> {
		const { url, parsedParams } = this.manageParams(api, endpoint, params);
		const headers = this.getPostHeaders();
		return this.httpClient.patch<T>(url, body, {
			headers,
			params: parsedParams,
		});
	}

	put$<T>(
		api: ApiType,
		endpoint: string,
		body: any,
		params?: any
	): Observable<T> {
		const { url, parsedParams } = this.manageParams(api, endpoint, params);
		const headers = this.getPostHeaders();
		return this.httpClient.put<T>(url, body, {
			headers,
			params: parsedParams,
		});
	}

	delete$<T>(api: ApiType, endpoint: string, params?: any): Observable<T> {
		const { url, parsedParams } = this.manageParams(api, endpoint, params);
		return this.httpClient.delete<T>(url, { params: parsedParams });
	}

	private manageParams(
		api: ApiType,
		endpoint: string,
		params?: any,
		query?: any[]
	) {
		let url = this.getPath(api, endpoint);
		const pathParams = this.pathParseService.getPathParams(url);
		url = this.pathParseService.replacePathParams(url, params);
		if (query) {
			url = this.manageQueryParamas(url, query);
		}
		const parsedParams = this.removePathParamsInParams(params, pathParams);
		return { url, parsedParams };
	}

	private manageQueryParamas(url: string, query: any[]) {
		let queryParams = "";
		query.forEach((obj) => {
			queryParams += `${Object.keys(obj)}=${Object.values(obj)[0]}&`;
		});
		const rs = `${url}?${queryParams}`;
		return rs.slice(0, -1);
	}

	private getPath(api: ApiType, endpointId: string): string {
		return `${environment.api[api]}${endpointId}`;
	}

	private getPostHeaders(): any {
		return {
			"Content-type": "application/json",
		};
	}

	private getFileUploadHeaders(): any {
		return {};
	}

	private removePathParamsInParams(params: any, pathParams: any) {
		const keys: any = _.keys(params);
		const paramsKeys = keys.filter(
			(key: any) => !pathParams.find((pp: any) => pp === key)
		);
		const parsedParams: any = {};
		paramsKeys.forEach((pk: any) => (parsedParams[pk] = params[pk]));
		return parsedParams;
	}

	/**
	 * Método encargado de gestionar los errores al realizar la petición de datos a la API
	 * @param operation Operación que ha generado el error
	 * @param result Resultado del error (Opcional)
	 */
	handleError<T>(operation = "operation", result?: T) {
		return (error: any): Observable<T> => {
			/*if(status == "400"){
        this.messageService.show(`Se ha producido un error en la solicitud`);
      }
      else if (status === "500"){
        this.messageService.show(`Se ha producido un error`);
      }
      else{
        this.messageService.show(`Se ha producido un error`);
      }*/
			// Let the app keep running by returning an empty result.
			return of(result as T);
		};
	}
}
